import {useState, useEffect, useRef, useCallback} from 'react';
import { useParams, useHistory, useRouteMatch, generatePath } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { Scrollbar } from 'react-scrollbars-custom';
import {OverlayTrigger} from 'react-bootstrap';
import { constants } from '../../../common/constants/constants';
import { getYearsOptions } from '../../../common/shared/statelessMethods';
import { fetchPaymentsByYear } from '../../../store/buildings/actions';
import { showLoader, hideLoader } from '../../../store/common/slice';
import { newSelectStyles } from '../../../common/shared/select.styled';
import PrintPdfDropdown from '../../../common/export_documents/PrintPdfDropdown';
import { useAccessToken } from '../../../common/hooks/useAccessToken';
import { createTotalRow, createPercentageRow } from '../../../common/utils/createTotalRows';
import { renderIcon, renderOneTimeIcon, renderTotalIcon, parseValues } from '../../../common/utils/yearlyRowsLogic';
import useBodyClass from '../../../common/hooks/useBodyClassName';
import Search from '../../../images/search.svg';
import { StyledReport } from '../report.styled';
import { StyledExpensesTooltip } from '../expensesMonthlyNew2/MonthlyExpensesNew.styled';
import { StyledYearlyByMonths } from './YearlyByMonths.styled';
import YearlyCard from './YearlyCard';
import {isAdminSelector} from "../../../store/auth/selectors";
import CommentsModal from "../../comments/CommentsModal";
import ManagerUpdateApartmentModal from "./ManagerUpdateApartmentModal";
import cloneDeep from 'lodash.clonedeep';
import {StopPlansModal} from "./StopPlansModal";
import { StyledPdfWrapper } from '../debtList/PdfWrapper.styled';
import YearlyPdfRow from './YearlyPdfRow';
import YearlyTenantPdfRow from './YearlyTenantPdfRow';
import { EditApartmentPaymentsModal } from './EditApartmentPaymentsModal';
import { PageTitle } from '../../../common/PageTitle';

const EXPORT_XLS_SETTINGS = {
    columnTypes: [
        { index: 1, type: 'Number' },
        { index: 2, type: 'Number' },
        { index: 3, type: 'Number' },
        { index: 4, type: 'Number' },
        { index: 5, type: 'Number' },
        { index: 6, type: 'Number' },
        { index: 7, type: 'Number' },
        { index: 8, type: 'Number' },
        { index: 9, type: 'Number' },
        { index: 10, type: 'Number' },
        { index: 11, type: 'Number' },
        { index: 12, type: 'Number' },
        { index: 13, type: 'Number' },
        { index: 14, type: 'Number' },
        { index: 15, type: 'Number' },
        { index: 16, type: 'Number' },
        { index: 17, type: 'Number' },
    ]
};

const YearlyByMonths = ({ address }) => {
    const isAdmin = useSelector(isAdminSelector);
    const { buildingID, year } = useParams();
    const { path } = useRouteMatch()
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const ltr = useSelector((state) => state.common.ltrEnabled);
    const client = useAccessToken();
    const history = useHistory();
    const yearOptions = getYearsOptions();

    const pdfContainer = useRef();
    const [pdfContainerActive, setPdfContainerActive] = useState(false);
    function pdfContainerActivate() {
        setPdfContainerActive(true);
    }

    const [selectedYear, setSelectedYear] = useState({
        label: year,
        value: parseInt(year)
    });
    const [records, setRecords] = useState([]);
    const [parsedRecords, setParsedRecords] = useState([]);
    const [totalRowData, setTotalRowData] = useState({});
    const [percentageRowData, setPercentageRowData] = useState();
    const [oneTimeHeaders, setOneTimeHeaders] = useState([]);
    const [oneTimeSum, setOneTimeSum] = useState();
    const [filter, setFilter] = useState('');
    const [apartments, setApartments] = useState([]);
    const [updatePayment, setUpdatePayment] = useState(false);

    const [commentsModalOpened, setCommentsModalOpened] = useState(false);
    const [commentsModalContext, setCommentsModalContext] = useState({});
    const onAptNotesClick = (buildingID, apartmentNumber) => {
        setCommentsModalContext({ buildingID, apartmentNumber })
        setCommentsModalOpened(true);
    };
    const onAptNotesClose = () => setCommentsModalOpened(false);

    const [managerUpdateModalOpened, setManagerUpdateModalOpened] = useState(false);
    const [managerUpdateModalContext, setManagerUpdateModalContext] = useState({});
    function managerUpdateCallback(context) {
        setManagerUpdateModalOpened(true);
        setManagerUpdateModalContext(context);
    }
    function managerUpdateClose() {
        setManagerUpdateModalOpened(false);
        setManagerUpdateModalContext({});
    }

    const [stopPlansModalOpened, setStopPlansModalOpened] = useState(false);
    const [stopPlansModalContext, setStopPlansModalContext] = useState({});

    const getExportData = useCallback((type) => {
        const formatNumbers = type === 'pdf';
        if (parsedRecords == null || parsedRecords.length !== records.length) {
            return [];
        }

        const icons = Object.values(constants.MonthsToText).map((month, i) => {
            const options = { percentage: percentageRowData && percentageRowData[month] };
            return [month, renderIcon(totalRowData[month], options, isAdmin, i, true)];
        });

        const paid = icons.reduce((acc, [month, icon]) => {
            const monthValue = icon.paid;
            acc[month] = formatNumbers ? parseValues(monthValue) : monthValue;
            return acc;
        }, {
            apartmentNumber: t('reports.paid_new'),
            totalFuture: formatNumbers ? parseValues(totalRowData?.total?.future.value) : totalRowData?.total?.future.value,
            oneTime: oneTimeHeaders.length > 0 ? oneTimeSum.paid.value : undefined,
        });

        const planned = icons.reduce((acc, [month, icon]) => {
            const monthValue = icon.planned;
            acc[month] = formatNumbers ? parseValues(monthValue) : monthValue;
            return acc;
        }, {
            apartmentNumber: t('reports.planned'),
            totalDebt: formatNumbers ? parseValues(totalRowData?.total?.debt.value) : totalRowData?.total?.debt.value,
            totalFuture: formatNumbers ? parseValues(totalRowData?.total?.future.value) : totalRowData?.total?.future.value,
        });

        const percentage = icons.reduce((acc, [month, icon]) => {
            const monthValue = icon.percentage;
            acc[month] = type === 'xls' ? { t: 's', v: monthValue, forced: true } : monthValue;
            return acc;
        }, {
            apartmentNumber: '%'
        });

        return [...parsedRecords, paid, planned, percentage];
    }, [parsedRecords, totalRowData, percentageRowData]);

    function stopPlansCallback(context) {
        setStopPlansModalOpened(true);
        setStopPlansModalContext(context);
    }
    function stopPlansClose(closed) {
        setStopPlansModalOpened(false);
        setStopPlansModalContext({});
        if (closed) {
            dispatch(showLoader());
            loadPayments(year, true);
            dispatch(hideLoader());
        }
    }

    const [changeEditApartmentPaymentsModalOpened, setChangeEditApartmentPaymentsModalOpened] = useState(false);
    const [editApartmentPaymentsModalContext, setEditApartmentPaymentsModalContext] = useState({});
    function editApartmentPaymentsCallback(context) {
        setChangeEditApartmentPaymentsModalOpened(true);
        setEditApartmentPaymentsModalContext(context);
    }

    function editApartmentPaymentsClose() {
        setChangeEditApartmentPaymentsModalOpened(false);
        setEditApartmentPaymentsModalContext({});
    }

    const monthTitles = constants.months.reduce((acc, month) => {
        acc[constants.MonthsToText[month]] = t(`common.months.${month}`)
        return acc
    }, {});

    const titleRow = [{
        apartmentNumber: t('reports.apartmentName'),
        ...monthTitles,
        oneTime: t('reports.oneTime'),
        totalCharged: t('reports.total_charged'),
        totalDebt: t('reports.total_debt'),
        totalFuture: t('reports.total_future')
    }];

    document.title = t('titles.yearly_report');
    useBodyClass('report');

    async function loadPayments(year, ignoreCache = false) {
        const { payload } = await dispatch(fetchPaymentsByYear({ buildingID, year, ignoreCache }));

        if (payload?.apartments) {
            let { rows: unsorted, headerExtras } = payload.apartments;
            const rows = [...unsorted]
                .map(r => cloneDeep(r)) // need clone as we modify this data but data is immutable
                .sort((a,b) => parseInt(a.apartmentNumber) < parseInt(b.apartmentNumber) ? -1 : 1);

            setRecords(rows);
            if (rows?.length > 0) {
                const currency = rows[0].total?.charged?.currency || constants.defaultCurrency;
                const totalRow = createTotalRow(rows, currency);
                totalRow['tenantName'] = t('reports.total');
                setTotalRowData(totalRow);
                const percentageRow = createPercentageRow(totalRow, currency);
                percentageRow['tenantName'] = t('reports.percentage');
                setPercentageRowData(percentageRow);
            }
            setOneTimeHeaders(headerExtras.filter(item => item.id).map(item => cloneDeep(item)));
        }


    }

    useEffect(async () => {
        if (client) {
            dispatch(showLoader());
            setParsedRecords([]);
            await loadPayments(year);
            const response = await client.tenants.getTenantsForBuilding(buildingID);
            setApartments(response?.apartments?.sort((a, b) => a.apartmentNum - b.apartmentNum));
            dispatch(hideLoader());
        }
    }, [client, year]);

    useEffect(async ()=>{
        if (updatePayment){
            dispatch(showLoader());
            await loadPayments(year, true);
            dispatch(hideLoader());
            setUpdatePayment(false);
            managerUpdateClose();
        }
    },[updatePayment]);

    const handleYearChanged = async year => {
        setSelectedYear(year);
        const pathname = generatePath(path, { buildingID, year: year.value });
        history.push({ pathname });
        // NOTE: payments will be loaded in another side effect dependent on year
    };

    const totalColumns = ['oneTime', 'total'];

    const csvCallback = item => setParsedRecords(prev => prev.concat([item]));

    function sumOneTime(arr) {
        const oneTimeSumObj = {
            amount_left: {value: 0, currency: ''},
            paid: {value: 0, currency: ''},
            scheduled_to_pay: {value: 0, currency: ''},
            total: {value: 0, currency: ''}
        };
        if (arr.length > 0) {
            for (const el of arr) {
                oneTimeSumObj.amount_left.value += el?.amount_left?.value ?? 0;
                oneTimeSumObj.total.value += el?.total?.value ?? 0;
                oneTimeSumObj.paid.value += el?.paid?.value ?? 0;
                oneTimeSumObj.scheduled_to_pay.value += el?.scheduled_to_pay?.value ?? 0;
                oneTimeSumObj.amount_left.currency = el?.amount_left?.currency;
                oneTimeSumObj.total.currency = el?.total?.currency;
                oneTimeSumObj.paid.currency = el?.paid?.currency;
                oneTimeSumObj.scheduled_to_pay.currency = el?.scheduled_to_pay?.currency;
            }
        }
        return oneTimeSumObj;
    }

    useEffect(() => {
        const recordsValues = Object.values(records);
        const oneTimeArrays = recordsValues.reduce((acc, record) => {
            Object.entries(record).forEach(([key, value]) => {
                if (key.includes('one_time')) acc.push(value)
            });
            return acc
        }, []);
        const sum = sumOneTime(oneTimeArrays);
        setOneTimeSum(sum);
    }, [records]);

    const renderRecords = (record, i, hideTooltip = false, renderCSV = true) => {
        return (
            <YearlyCard
                record={record}
                buildingID={buildingID}
                oneTimeHeaders={oneTimeHeaders}
                filter={filter}
                csvCallback={csvCallback}
                aptNotesClickCallback={onAptNotesClick}
                aptStopPlansCallback={stopPlansCallback}
                managerUpdateCallback={managerUpdateCallback}
                editApartmentPaymentsCallback={editApartmentPaymentsCallback}
                hideTooltip={hideTooltip}
                year={year}
                selectedYear={selectedYear}
                renderCSV={renderCSV}
                key={i}
            />
        )
    };

    const renderPdfRows = (record, i) => <YearlyPdfRow record={record} oneTimeHeaders={oneTimeHeaders} key={i} />;

    const onMonthHeaderClick = month => history.push({ pathname: `/${constants.pages.monthlyCashFlowUrl}/${buildingID}/${month}/${year}` });

    const renderTenantRows = (apt, i) => <YearlyTenantPdfRow apt={apt} key={i} />;

    return (
        <StyledYearlyByMonths ltr={ltr}>
            <PageTitle text={t('reports.yearly_by_months_title_new')}>
                <PrintPdfDropdown
                    classes='report-pdf-icons'
                    buildingID={buildingID}
                    eventLabel="yearlyByMonth"
                    titleString={`${selectedYear.value} ${t('reports.yearly_by_months_title', { year: '' })}`}
                    excelTitle={`${selectedYear.value} ${t('reports.yearly_by_months_title', { year: '' })}`}
                    reportName={t('reports.yearly_by_months_title_new')}
                    pdfContainer={pdfContainer.current}
                    onClick={pdfContainerActivate}
                    rows={getExportData}
                    titleRow={titleRow}
                    xlsSettings={EXPORT_XLS_SETTINGS}
                    pdfIframe
                    pdfSettings={{chunkSize: 18, orientation: constants.pages.pdfOrientation.landscape}}
                    address={address}
                />
            </PageTitle>

            <StyledReport className='report' ltr={ltr}>
                <div className='report-wrap'>
                    <div className='report-top'>
                        <div className='report-top-start'>
                            <div className="search">
                                <input
                                    type="text"
                                    name="search"
                                    onChange={e => setFilter(e.target.value.toLowerCase())}
                                    value={filter}
                                />
                                <img src={Search} alt={t('search.search')} />
                            </div>
                            <Select
                                className='select'
                                styles={newSelectStyles}
                                options={yearOptions}
                                value={selectedYear}
                                onChange={handleYearChanged}
                                isRtl={!ltr}
                            />
                        </div>
                    </div>
                </div>
            </StyledReport>

            <div className='scroll-container'>
                <div className='column-titles'>
                    <span>{t('reports.apt')}</span>
                    {constants.months.map((month, i) => {
                        return (
                            <OverlayTrigger
                                key={i}
                                placement="top"
                                overlay={props => (
                                    <StyledExpensesTooltip {...props}>
                                        <div>
                                            {t(`common.months.${month}`)}
                                        </div>
                                    </StyledExpensesTooltip>
                                )}
                            >
                                <span className='month-header' onClick={() => onMonthHeaderClick(month)}>{t(`common.months.${month}`)}</span>
                            </OverlayTrigger>
                        )
                    })}
                    {totalColumns.map((total, i) => {
                        return (
                            <OverlayTrigger
                                key={i}
                                placement="top"
                                overlay={props => (
                                    <StyledExpensesTooltip {...props}>
                                        <div>
                                            {t(`reports.${total}`)}
                                        </div>
                                    </StyledExpensesTooltip>
                                )}
                            >
                                <span className='total-title'>{t(`reports.${total}`)}</span>
                            </OverlayTrigger>
                        )
                    })}
                </div>
                <Scrollbar
                    style={{ maxWidth: '100%', height: 'calc(100vh - 250px)' }}
                    trackYProps={{
                        renderer: props => {
                            const { elementRef, ...restProps } = props;
                            return <div {...restProps} ref={elementRef} className="scroll-track" />;
                        }
                    }}
                    thumbYProps={{
                        renderer: props => {
                            const { elementRef, ...restProps } = props;
                            return <div {...restProps} ref={elementRef} className="scroll-thumb" />;
                        }
                    }}
                    noScrollX
                    rtl={false}
                >
                    <div className='cards-wrapper'>
                        {records?.map((record, i) => renderRecords(record, i))}
                    </div>
                </Scrollbar>
            </div>
            <div className='total-row'>
                <span>{t('reports.total_to_pay')}</span>
                {Object.values(constants.MonthsToText).map((month, i) => renderIcon(totalRowData[month], {percentage: percentageRowData && percentageRowData[month]}, isAdmin, i))}
                <span className='one-time'>{oneTimeHeaders.length > 0 && renderOneTimeIcon(oneTimeSum, isAdmin)}</span>
                {renderTotalIcon(totalRowData.total)}
            </div>

            <div id="section-to-print" ref={pdfContainer}>
                {pdfContainerActive && (
                    <StyledPdfWrapper ltr={ltr}>
                        <div className='pdf-title'>{`${year} ${t('reports.yearly_collection_report')} ${address}`}</div>

                        <div className='pdf-rows-wrapper'>
                            <div className='pdf-row'>
                                <div className='yearly-apt'>{t('reports.apt')}</div>
                                {constants.months.map((month, i) => {
                                    return <div className='yearly' key={i}>{t(`common.monthsShort.${month}`)}</div>
                                })}
                                <div className='yearly'>{t('reports.special')}</div>
                                <div className='yearly'>{t('reports.charged')}</div>
                                <div className='yearly'>{t('reports.debt')}</div>
                                <div className='yearly'>{t('reports.future')}</div>
                            </div>

                            {records?.map((record, i) => renderPdfRows(record, i))}

                            <div className='pdf-row'>
                                <div className='yearly-apt pdf-highlited'>{t('reports.total_charged')}</div>
                                {Object.values(constants.MonthsToText).map((month, i) => {
                                    return (
                                        <div className='yearly'>
                                            {renderIcon(totalRowData[month], {percentage: percentageRowData && percentageRowData[month]}, isAdmin, i, true).paid}
                                        </div>
                                    )
                                })}
                                <div className='yearly'>{oneTimeHeaders.length > 0 && oneTimeSum.paid.value}</div>
                                <div className='yearly'>{parseValues(totalRowData?.total?.charged.value)}</div>
                            </div>

                            <div className='pdf-row'>
                                <div className='yearly-apt pdf-highlited'>{t('reports.total_to_pay')}</div>
                                {Object.values(constants.MonthsToText).map((month, i) => {
                                    return (
                                        <div className='yearly'>
                                            {renderIcon(totalRowData[month], {percentage: percentageRowData && percentageRowData[month]}, isAdmin, i)}
                                        </div>
                                    )
                                })}
                                <div className='yearly'>{oneTimeHeaders.length > 0 && oneTimeSum.total.value}</div>
                                <div className='yearly'>{renderTotalIcon(totalRowData.total)}</div>
                                <div className='yearly'>{parseValues(totalRowData?.total?.debt.value)}</div>
                                <div className='yearly'>{parseValues(totalRowData?.total?.future.value)}</div>
                            </div>

                            <div className='pdf-row'>
                                <div className='yearly-apt pdf-highlited'>%</div>
                                {Object.values(constants.MonthsToText).map((month, i) => {
                                    return (
                                        <div className='yearly'>
                                            {renderIcon(totalRowData[month], {percentage: percentageRowData && percentageRowData[month]}, isAdmin, i, true).percentage}
                                        </div>
                                    )
                                })}
                            </div>

                            <div className='pdf-note'>{t('reports.pdf_note')}</div>

                        </div>
                        <div className='pdf-title'>{t('reports.tenant_list_title')} <span>{address}</span></div>

                        <div className='pdf-rows-wrapper'>
                            <div className='pdf-row'>
                                <div className='yearly-apt'>{t('reports.apt')}</div>
                                <div className='yearly-apt'>{t('payments.renter')}</div>
                                <div className='yearly-apt'>{t('reports.tenantPhone')}</div>
                                <div className='yearly-apt'>{t('reports.tenantEmail')}</div>
                                <div className='yearly-apt'>{t('payments.owner')}</div>
                                <div className='yearly-apt'>{t('reports.tenantPhone')}</div>
                                <div className='yearly-apt'>{t('reports.tenantEmail')}</div>
                                <div className='yearly-apt'>{t('reports.monthlyFee')}</div>
                            </div>

                            {apartments.map((apt, i) => renderTenantRows(apt, i))}
                        </div>
                    </StyledPdfWrapper>
                )}
            </div>

            <CommentsModal
                show={commentsModalOpened}
                buildingId={commentsModalContext.buildingID}
                onModalHide={onAptNotesClose}
                apartmentNum={commentsModalContext.apartmentNumber}
                prefetchComments={false}
            />

            <ManagerUpdateApartmentModal
                show={managerUpdateModalOpened}
                onHide={managerUpdateClose}
                row={managerUpdateModalContext.record}
                buildingID={managerUpdateModalContext.buildingID}
                year={managerUpdateModalContext.year}
                selectedYear={managerUpdateModalContext.selectedYear}
                setUpdatePayment = {setUpdatePayment}
            />

            <StopPlansModal show={stopPlansModalOpened} onHide={stopPlansClose} {...stopPlansModalContext} />
            <EditApartmentPaymentsModal show={changeEditApartmentPaymentsModalOpened} onHide={editApartmentPaymentsClose} {...editApartmentPaymentsModalContext} setUpdatePayment = {setUpdatePayment}/>

        </StyledYearlyByMonths>
    );
};

export default YearlyByMonths;
